import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  adminCrawlerApi,
  type CrawlerSeedItem,
} from '../../services/api';

const SOURCE_LABELS: Record<string, string> = {
  web_search: 'Web search',
  manual: 'Manual',
  import: 'Import',
};

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function downloadText(filename: string, mime: string, content: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Crawler seed list management panel.
 *
 * Seeds are persisted URLs fed to the crawler queue as single-page crawls
 * (no link expansion). Rows with source "Web search" were auto-captured from
 * chat web-search fallback citations — pages the assistant grounded on when
 * the library came up empty, queued so the gap gets indexed.
 */
export const CrawlerSeedsSection: React.FC = () => {
  const [seeds, setSeeds] = useState<CrawlerSeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchSeeds = useCallback(async () => {
    setError(null);
    try {
      const items = await adminCrawlerApi.listSeeds({
        search: search || undefined,
        source: sourceFilter || undefined,
      });
      setSeeds(items);
    } catch {
      setError('Failed to load crawler seeds.');
    } finally {
      setLoading(false);
    }
  }, [search, sourceFilter]);

  useEffect(() => {
    const t = setTimeout(fetchSeeds, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchSeeds, search]);

  const handleAdd = async () => {
    if (!newUrl.trim()) return;
    setAdding(true);
    setError(null);
    try {
      await adminCrawlerApi.createSeed({
        url: newUrl.trim(),
        notes: newNotes.trim() || undefined,
      });
      setNewUrl('');
      setNewNotes('');
      setNotice('Seed added.');
      await fetchSeeds();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Failed to add seed.');
    } finally {
      setAdding(false);
    }
  };

  const handleToggleStatus = async (seed: CrawlerSeedItem) => {
    setBusyId(seed.id);
    try {
      const updated = await adminCrawlerApi.updateSeed(seed.id, {
        status: seed.status === 'active' ? 'paused' : 'active',
      });
      setSeeds((prev) => prev.map((s) => (s.id === seed.id ? updated : s)));
    } catch {
      setError('Failed to update seed.');
    } finally {
      setBusyId(null);
    }
  };

  const handleEnqueue = async (seed: CrawlerSeedItem) => {
    setBusyId(seed.id);
    setError(null);
    try {
      const updated = await adminCrawlerApi.enqueueSeed(seed.id);
      setSeeds((prev) => prev.map((s) => (s.id === seed.id ? updated : s)));
      setNotice(`Enqueued ${seed.url} for crawling.`);
    } catch {
      setError('Failed to enqueue seed (crawler queue may be unavailable).');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (seed: CrawlerSeedItem) => {
    if (!window.confirm(`Delete seed ${seed.url}? Already-crawled documents are kept.`)) return;
    setBusyId(seed.id);
    try {
      await adminCrawlerApi.deleteSeed(seed.id);
      setSeeds((prev) => prev.filter((s) => s.id !== seed.id));
    } catch {
      setError('Failed to delete seed.');
    } finally {
      setBusyId(null);
    }
  };

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      const content = await adminCrawlerApi.exportSeeds(format);
      downloadText(
        format === 'csv' ? 'crawler-seeds.csv' : 'crawler-seeds.json',
        format === 'csv' ? 'text/csv' : 'application/json',
        content,
      );
    } catch {
      setError('Failed to export seeds.');
    }
  };

  const handleImportFile = async (file: File) => {
    setError(null);
    try {
      const text = await file.text();
      const result = await adminCrawlerApi.importSeeds(text);
      setNotice(
        `Imported ${result.imported} seed(s); skipped ${result.skipped_existing} existing, ${result.skipped_invalid} invalid.`,
      );
      await fetchSeeds();
    } catch {
      setError('Failed to import seeds. Expecting CSV with columns: url[,title,notes].');
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight text-primary">Crawler Seeds</h2>
          <p className="text-xs text-muted-foreground">
            Single-page crawl targets. “Web search” rows were cited by chat web fallback answers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => handleExport('csv')}>
            Export CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleExport('json')}>
            Export JSON
          </Button>
          <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
            Import CSV
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleImportFile(file);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {notice && (
          <Alert>
            <AlertDescription>{notice}</AlertDescription>
          </Alert>
        )}

        {/* Add seed */}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="https://www.cms.gov/…"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            className="max-w-md"
          />
          <Input
            placeholder="Notes (optional)"
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
            className="max-w-xs"
          />
          <Button
            size="sm"
            className="bg-primary text-primary-foreground shadow-none hover:bg-primary/90"
            onClick={handleAdd}
            disabled={adding || !newUrl.trim()}
          >
            {adding ? <Spinner size="sm" /> : 'Add seed'}
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search URL or title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
          />
          <Select
            value={sourceFilter || '__all__'}
            onValueChange={(v) => setSourceFilter(v === '__all__' ? '' : v)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sources</SelectItem>
              <SelectItem value="web_search">Web search</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="import">Import</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 text-left text-xs font-medium text-muted-foreground">
                <th className="py-2 pr-2">URL</th>
                <th className="py-2 pr-2">Source</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2 pr-2">Cited</th>
                <th className="py-2 pr-2">Last enqueued</th>
                <th className="py-2 pr-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-muted-foreground">
                    <Spinner size="sm" />
                  </td>
                </tr>
              ) : seeds.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-muted-foreground">
                    No seeds yet. Web-fallback citations will appear here automatically.
                  </td>
                </tr>
              ) : (
                seeds.map((seed) => (
                  <tr key={seed.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-2 max-w-[380px]">
                      <a
                        href={seed.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-primary hover:underline"
                        title={seed.title || seed.url}
                      >
                        {seed.url}
                      </a>
                      {seed.notes && (
                        <div className="text-xs text-muted-foreground truncate">{seed.notes}</div>
                      )}
                    </td>
                    <td className="py-2 pr-2 whitespace-nowrap">
                      {SOURCE_LABELS[seed.source] ?? seed.source}
                    </td>
                    <td className="py-2 pr-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          seed.status === 'active'
                            ? 'bg-primary/10 text-primary'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {seed.status}
                      </span>
                    </td>
                    <td className="py-2 pr-2">{seed.times_cited || 0}</td>
                    <td className="py-2 pr-2 whitespace-nowrap">{formatDate(seed.last_enqueued_at)}</td>
                    <td className="py-2 pr-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === seed.id || seed.status !== 'active'}
                          onClick={() => handleEnqueue(seed)}
                        >
                          Enqueue
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === seed.id}
                          onClick={() => handleToggleStatus(seed)}
                        >
                          {seed.status === 'active' ? 'Pause' : 'Activate'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive"
                          disabled={busyId === seed.id}
                          onClick={() => handleDelete(seed)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CrawlerSeedsSection;
